import { useState, useEffect, useCallback, useRef } from 'react'
import type { IntegrationSummary } from '../../integrations/types/index'
import { getSlackOAuthUrl, disconnectSlack } from '../services/slackApi'

type ActionStatus = 'idle' | 'connecting' | 'disconnecting' | 'error'

const CHANNEL_NAME = 'slack_oauth'

// Maps the `reason` code the backend puts on the OAuth error redirect to a
// message the user can act on. Unknown reasons fall back to the generic string.
const REASON_MESSAGES: Record<string, string> = {
  access_denied: 'You declined the Slack authorization. Try again when ready.',
  missing_params: 'Slack did not complete the authorization. Please try again.',
  invalid_state: 'That connection link was invalid. Please start again from Connect Workspace.',
  state_expired: 'The connection request timed out. Please try again.',
  oauth_failed: 'Slack rejected the authorization — the app may be misconfigured. Contact your administrator.',
  bad_redirect_uri: 'Slack rejected the redirect URL — the Slack app is misconfigured. Contact your administrator.',
  redirect_uri_mismatch: 'Slack rejected the redirect URL — the Slack app is misconfigured. Contact your administrator.',
  invalid_code: 'The authorization expired before it could be used. Please try again.',
  code_already_used: 'That authorization was already used. Please start again from Connect Workspace.',
  invalid_client_id: 'The Slack app credentials are invalid. Contact your administrator.',
  bad_client_secret: 'The Slack app credentials are invalid. Contact your administrator.',
  verification_failed: 'Slack could not verify the new connection. Please try again.',
  slack_team_already_connected: 'This Slack workspace is already connected to another Zeph workspace.',
  slack_unreachable: 'Could not reach Slack just now. Please try again in a moment.',
  encryption_failed: 'Server configuration issue prevented saving the connection. Contact your administrator.',
  persist_failed: 'Could not save the Slack connection. Please try again.',
  slack_not_configured: 'Slack is not configured on this server. Contact your administrator.',
}

function messageForReason(reason?: string | null): string {
  return (reason && REASON_MESSAGES[reason]) || 'Slack connection failed. Please try again.'
}

export function useSlackIntegration(workspaceId: string, summary: IntegrationSummary | null, onRefresh: () => void) {
  const [actionStatus, setActionStatus] = useState<ActionStatus>('idle')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const popupRef = useRef<Window | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const clearError = useCallback(() => setErrorMessage(null), [])

  // Primary: BroadcastChannel (works even when window.opener is null after redirects)
  useEffect(() => {
    let channel: BroadcastChannel | null = null
    try {
      channel = new BroadcastChannel(CHANNEL_NAME)
      channel.onmessage = (event: MessageEvent) => {
        if (event.data?.type !== 'slack_oauth') return
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null }
        if (event.data.result === 'connected') {
          setActionStatus('idle')
          setErrorMessage(null)
          onRefresh()
        } else {
          setActionStatus('error')
          setErrorMessage(messageForReason(event.data.reason))
        }
      }
    } catch {
      // BroadcastChannel not supported — fall back to postMessage listener below
    }
    return () => { channel?.close() }
  }, [onRefresh])

  // Fallback: window.postMessage
  useEffect(() => {
    function onMessage(event: MessageEvent) {
      if (event.origin !== window.location.origin) return
      if (event.data?.type !== 'slack_oauth') return
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null }
      if (event.data.result === 'connected') {
        setActionStatus('idle')
        setErrorMessage(null)
        onRefresh()
      } else {
        setActionStatus('error')
        setErrorMessage(messageForReason(event.data.reason))
      }
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [onRefresh])

  const connect = useCallback(async () => {
    setActionStatus('connecting')
    clearError()
    try {
      const url = await getSlackOAuthUrl(workspaceId)
      const popup = window.open(url, 'slack_oauth', 'width=600,height=700,left=200,top=100')
      popupRef.current = popup

      pollRef.current = setInterval(() => {
        if (popup?.closed) {
          if (pollRef.current) clearInterval(pollRef.current)
          setActionStatus((s) => s === 'connecting' ? 'idle' : s)
        }
      }, 500)
    } catch {
      setActionStatus('error')
      setErrorMessage('Could not start Slack authorization. Please try again.')
    }
  }, [workspaceId, clearError])

  const disconnect = useCallback(async () => {
    setActionStatus('disconnecting')
    clearError()
    try {
      await disconnectSlack(workspaceId)
      setActionStatus('idle')
      onRefresh()
    } catch {
      setActionStatus('error')
      setErrorMessage('Failed to disconnect Slack. Please try again.')
    }
  }, [workspaceId, clearError, onRefresh])

  return {
    actionStatus,
    errorMessage,
    clearError,
    connect,
    reconnect: connect,
    disconnect,
  }
}
